import { CloseOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Modal, Space, Typography } from "antd";
import { useFetchDebtorsQuery } from "features/debtorProfile/debtorProfileAPI";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { CALL_STATES, useInternalDialer } from "../internalDialerContext";
import useIncomingCallRingtone from "../hooks/useIncomingCallRingtone";

const { Title, Text } = Typography;

const StyledModalContent = styled.div`
  text-align: center;
`;

const StyledAvatar = styled(Avatar)`
  background-color: #1677ff;
  margin-bottom: 16px;
`;

const StyledActionRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 24px;
`;

const StyledSecondaryRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
`;

// Ant Design 5 renders the modal mask as a sibling of the dialog
// (both children of the portal container), so a styled(Modal)
// descendant selector targeting `.ant-modal-mask` never matches and
// the blur is silently dropped. The `styles` prop is the supported
// way to inject styles directly onto the mask node.
const MASK_STYLE = {
  background: "rgba(0, 0, 0, 0.45)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};

const CONTENT_STYLE = {
  padding: "32px 24px",
};

function IncomingCallModal() {
  const {
    callState,
    incomingCallId,
    incomingCallerPhone,
    isMinimized,
    acceptIncoming,
    declineIncoming,
    minimizeIncomingModal,
  } = useInternalDialer();
  const navigate = useNavigate();

  // The modal follows the SDK's own source of truth -- the presence
  // of an incomingCallId -- rather than the callState enum, because a
  // transient callState transition must never close the modal while a
  // real invite is still alive. The ringtone follows the same signal.
  const isIncoming = !!incomingCallId && callState === CALL_STATES.INCOMING;

  useIncomingCallRingtone(isIncoming ? CALL_STATES.INCOMING : CALL_STATES.IDLE);

  // Deterministic phone lookup via DebtorListApi. The new `phones`
  // filter on DebtorFiltersInput matches against the raw_number on
  // DebtorContactPhone via clean_phone, so we get an exact match
  // rather than a fuzzy / possibly-stale OpenSearch result. If
  // multiple debtors share the same number the BE routing path picks
  // the assigned-collector one and logs the DCH against that debtor.
  const trimmedPhone = (incomingCallerPhone ?? "").trim();
  const { data: debtorListPage } = useFetchDebtorsQuery(
    { filters: { phones: [trimmedPhone] }, limit: 1 },
    { skip: !isIncoming || !trimmedPhone },
  );

  const debtor = debtorListPage?.results?.[0] ?? null;
  const displayName = debtor?.name || debtor?.fullName || "Unknown Caller";
  const displayPhone = incomingCallerPhone || "";

  const handleOpenDebtorPage = () => {
    if (!debtor?.id) return;
    minimizeIncomingModal();
    navigate(`/debtors/${debtor.id}`);
  };

  return (
    <Modal
      open={isIncoming && !isMinimized}
      maskClosable={false}
      closable={false}
      footer={null}
      centered
      width={420}
      styles={{ mask: MASK_STYLE, content: CONTENT_STYLE }}
    >
      <StyledModalContent>
        <StyledAvatar size={96} icon={<UserOutlined />} />
        <Title level={3} style={{ margin: 0 }}>
          {displayName}
        </Title>
        <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
          {displayPhone && <Text type="secondary">{displayPhone}</Text>}
          {debtor?.externalId && <Text type="secondary">External ID: {debtor.externalId}</Text>}
          <Text type="secondary">Incoming call...</Text>
        </Space>
        <StyledActionRow>
          <Button
            shape="circle"
            size="large"
            type="primary"
            icon={<PhoneOutlined />}
            style={{ width: 64, height: 64, background: "#52c41a", borderColor: "#52c41a" }}
            onClick={() => acceptIncoming(incomingCallId)}
          />
          <Button
            shape="circle"
            size="large"
            danger
            type="primary"
            icon={<PhoneOutlined style={{ transform: "rotate(135deg)" }} />}
            style={{ width: 64, height: 64 }}
            onClick={() => declineIncoming(incomingCallId)}
          />
        </StyledActionRow>
        <StyledSecondaryRow>
          {debtor?.id && (
            <Button type="link" onClick={handleOpenDebtorPage}>
              Open debtor page
            </Button>
          )}
          <Button type="text" icon={<CloseOutlined />} onClick={minimizeIncomingModal}>
            Minimize
          </Button>
        </StyledSecondaryRow>
      </StyledModalContent>
    </Modal>
  );
}

export default IncomingCallModal;
